/**
 * useCognito
 * Amazon Cognito (Amplify) の認証情報を取得し、一元的に取得するためのカスタムフック
 */

// npm packages
import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAtom } from 'jotai';
import { Auth, Hub } from 'aws-amplify';
import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { HubCallback } from '@aws-amplify/core/lib/Hub';
import { useQuery } from '@tanstack/react-query';

// apis
import { getMember, getRole } from '@/apis/core/user';
import { GetMember200ResponseBody, GetRole200ResponseBody } from 'openapi/omise-craft-core-spec';

// stores
import { AuthContextSingleton, initialAuthContext, AuthContextType } from '@/stores/authContext';
import { reAgreeAtom } from '@/stores/session/ReAgreeAtom';
import userRoleAtom from '@/stores/session/UserRoleAtom';
import useConfigContext from '@/config/context';

const getSession = (): Promise<CognitoUserSession | null> => Auth.currentSession();

const canNotContinue = (authCtx: AuthContextType) => {
  return (!authCtx.cid || authCtx.cid === '') && (!authCtx.accessToken || authCtx.accessToken !== '');
};

type AmplifyCurrentUserInfo = {
  username: string;
};

const toAmplifyCurrentUserInfo = (value: unknown): AmplifyCurrentUserInfo | null => {
  if (typeof value !== 'object' || value === null) {
    return null;
  }

  const record = value as Record<string, unknown>;
  const username = record['username'];
  if (typeof username !== 'string') {
    return null;
  }

  return { username };
};

/**
 * useCognito: Amazon Cognito (Amplify) カスタムフック
 */
export const useCognito = () => {
  const authContext = AuthContextSingleton.getInstance();
  const [authCtx, setAuthCtx] = useAtom(authContext.authContextAtom);
  const [, setReAgreeStatus] = useAtom(reAgreeAtom);

  const [, setUserRole] = useAtom(userRoleAtom);

  const memberQuery = useQuery<GetMember200ResponseBody, Error>(['get_member'], () => getMember(undefined), {
    enabled: false
  });

  const roleQuery = useQuery<GetRole200ResponseBody | undefined, Error>(['get_role'], () => getRole(undefined), {
    enabled: false
  });

  const navigate = useNavigate();
  const location = useLocation();
  const config = useConfigContext();

  const prevLocationPathRef = useRef<string>('');

  const isTermsReAgreePath = (pathname: string): boolean => pathname.includes('/termsReAgree');

  const redirectToTermsReAgreeIfNeeded = (roleId: string, hasNeedReAgree: boolean | undefined): void => {
    if (config?.siteMode === 'operator') {
      return;
    }
    if (isTermsReAgreePath(location.pathname)) {
      return;
    }

    // 登録済み利用者(roleId === '2') 以外は再同意対象外
    if (roleId !== '2') {
      return;
    }

    if (!hasNeedReAgree) {
      return;
    }

    setReAgreeStatus(true);
    navigate('/termsReAgree');
  };

  // ログインそのものに成功した状態
  // ここからサービスAPIに対して、ユーザ情報を取得する
  useEffect(() => {
    if (canNotContinue(authCtx)) {
      return;
    }

    // 初期化が終わっていないタイミングの遷移追従はしない（初期化useEffectで確定させる）
    if (!authCtx.isAuthInitialized) {
      return;
    }

    // sitemode が 'operator' の場合は、再同意チェックを行わない
    if (config?.siteMode === 'operator') {
      // 管理モードでもロール情報は取ること
      (async () => {
        const roleData = (await roleQuery.refetch()).data;
        const roleId = roleData?.role_id?.toString() ?? '1';
        setUserRole(roleId);
        setAuthCtx({
          cid: authCtx.cid,
          uid: authCtx.uid,
          accessToken: authCtx.accessToken,
          role: roleId,
          member: null,
          isAuthInitialized: true
        });
      })();
      return;
    }

    (async () => {
      if (isTermsReAgreePath(location.pathname)) {
        return;
      }
      if (isTermsReAgreePath(prevLocationPathRef.current)) {
        // 直前のパスが再同意画面の場合は、2秒待ってから再同意チェックを行う
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      prevLocationPathRef.current = location.pathname;

      const member = (await memberQuery.refetch()).data;
      const roleData = (await roleQuery.refetch()).data;
      const roleId = roleData?.role_id?.toString() ?? '1';

      // 一般ユーザの場合
      setAuthCtx({
        cid: authCtx.cid,
        uid: member?.id.toString() ?? '',
        accessToken: authCtx.accessToken,
        role: roleId,
        member: member ?? null,
        isAuthInitialized: true
      });
      setUserRole(roleId);

      redirectToTermsReAgreeIfNeeded(roleId, member?.hasNeedReAgree);
    })();
  }, [location.key, authCtx.isAuthInitialized]);

  // amplifyの認証イベントにフックし、付随する動作を設定する
  const authListener: HubCallback = ({ payload: { event, data } }) => {
    try {
      switch (event) {
        case 'signIn':
          setAuthCtx({
            cid: data?.username ?? '',
            uid: authCtx.uid,
            accessToken: data?.signInUserSession?.accessToken?.jwtToken ?? '',
            role: authCtx.role,
            member: authCtx.member,
            isAuthInitialized: true
          });
          break;

        // ログイン成功以外はぜーんぶログアウト扱い
        case 'signIn_failure':
        case 'signOut':
        default:
          setReAgreeStatus(false);
          setAuthCtx(initialAuthContext());
          break;
      }
    } catch (error) {
      // 処理ミスった時は強制的にロールを1に設定
      console.error(error);
      setReAgreeStatus(false);
      setAuthCtx(initialAuthContext());
    }
  };

  // 再読み込み時等処理
  useEffect(() => {
    (async () => {
      // mark initialization start
      setAuthCtx({
        cid: authCtx.cid,
        uid: authCtx.uid,
        accessToken: authCtx.accessToken,
        role: authCtx.role,
        member: authCtx.member,
        isAuthInitialized: false
      });

      try {
        const session = await getSession();
        if (session && session.isValid()) {
          // まず current user を取得
          const userInfoUnknown: unknown = await Auth.currentUserInfo();
          const user = toAmplifyCurrentUserInfo(userInfoUnknown);
          if (!user) {
            throw new Error('Auth.currentUserInfo() returned invalid shape');
          }

          // 管理モードの場合は member の参照は行わず role のみ取得
          if (config?.siteMode === 'operator') {
            const roleRes = await roleQuery.refetch();
            const roleId = roleRes?.data?.role_id?.toString() ?? '1';
            setUserRole(roleId);
            setAuthCtx({
              cid: user.username,
              uid: authCtx.uid,
              accessToken: session.getAccessToken().getJwtToken(),
              role: roleId,
              member: null,
              isAuthInitialized: true
            });
            return;
          }

          // role と member は並列で取得して待つ
          const [roleRes, memberRes] = await Promise.all([roleQuery.refetch(), memberQuery.refetch()]);
          const roleId = roleRes?.data?.role_id?.toString() ?? '1';
          const member = memberRes?.data ?? null;

          setUserRole(roleId);
          // member と role を揃えて一度だけセットする
          setAuthCtx({
            cid: user.username,
            uid: member?.id?.toString() ?? '',
            accessToken: session.getAccessToken().getJwtToken(),
            role: roleId,
            member: member,
            isAuthInitialized: true
          });

          // 再同意ロジック（memberが必要）
          redirectToTermsReAgreeIfNeeded(roleId, member?.hasNeedReAgree);

          return;
        }

        // セッションなし（未ログイン）でも初期化完了は通知する
        setAuthCtx({ ...initialAuthContext(), isAuthInitialized: true });
      } catch (error) {
        console.error('session error', error);
        // エラー時も初期化完了を通知して待ちを解除する
        setAuthCtx({ ...initialAuthContext(), isAuthInitialized: true });
      }
    })();
  }, []);

  useEffect(() => {
    Hub.listen('auth', authListener);
    return () => Hub.remove('auth', authListener);
  }, []);

  return authCtx;
};
